import { useState, useCallback, useEffect, useRef } from "react"
import { email_domains_health_checks_path } from "~/lib/routes"
import { getCsrfToken } from "~/lib/utils"

type TriggerOptions = {
  emailAccountIds?: number[]
  domain?: string
}

type UseHealthCheckOptions = {
  /** When this value changes after triggering, the running state clears automatically */
  watchValue?: unknown
  /** When health_check_status changes from "pending" to null/completed, the running state clears automatically */
  healthCheckStatus?: "pending" | "completed" | null
  /** Maximum duration in ms to wait for the check to complete (default: 60000) */
  maxDuration?: number
}

const DEFAULT_MAX_DURATION = 60000 // 60 seconds

export function useHealthCheck(options: UseHealthCheckOptions = {}) {
  const { watchValue, healthCheckStatus, maxDuration = DEFAULT_MAX_DURATION } = options
  const [isRunning, setIsRunning] = useState(false)
  const triggeredRef = useRef(false)
  const initialWatchValueRef = useRef<unknown>(undefined)
  const previousHealthCheckStatusRef = useRef<"pending" | "completed" | null | undefined>(undefined)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear running state when watchValue changes after triggering
  useEffect(() => {
    if (triggeredRef.current && isRunning && watchValue !== initialWatchValueRef.current) {
      setIsRunning(false)
      triggeredRef.current = false
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [watchValue, isRunning])

  // Clear running state when health_check_status changes from "pending" to null/completed
  useEffect(() => {
    if (
      triggeredRef.current &&
      isRunning &&
      previousHealthCheckStatusRef.current === "pending" &&
      healthCheckStatus !== "pending" &&
      healthCheckStatus !== undefined
    ) {
      setIsRunning(false)
      triggeredRef.current = false
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
    // Update the previous status for next comparison
    previousHealthCheckStatusRef.current = healthCheckStatus
  }, [healthCheckStatus, isRunning])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const triggerHealthCheck = useCallback(
    async (triggerOptions: TriggerOptions) => {
      const { emailAccountIds, domain } = triggerOptions

      setIsRunning(true)
      triggeredRef.current = true
      initialWatchValueRef.current = watchValue
      previousHealthCheckStatusRef.current = healthCheckStatus

      // Set a timeout to auto-clear the running state
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        if (triggeredRef.current) {
          setIsRunning(false)
          triggeredRef.current = false
        }
      }, maxDuration)

      try {
        const body: Record<string, unknown> = {}
        if (emailAccountIds && emailAccountIds.length > 0) {
          body.email_account_ids = emailAccountIds
        }
        if (domain) {
          body.domain = domain
        }

        const response = await fetch(email_domains_health_checks_path(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          credentials: "same-origin",
          body: JSON.stringify(body),
        })

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: i18n.t("js.unknown_error") }))
          throw new Error(error.error || i18n.t("js.email_accounts_center.errors.health_check_failed"))
        }

        await response.json()
        return true
      } catch (error) {
        console.error("Health check failed:", error)
        App.displayFlash(
          "error",
          error instanceof Error ? error.message : i18n.t("js.email_accounts_center.errors.health_check_failed"),
        )
        setIsRunning(false)
        triggeredRef.current = false
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
        return false
      }
    },
    [watchValue, healthCheckStatus, maxDuration],
  )

  const stopRunning = useCallback(() => {
    setIsRunning(false)
    triggeredRef.current = false
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  return {
    isRunning,
    triggerHealthCheck,
    stopRunning,
  }
}
